import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { getMonthName } from '../../../core/helper/month';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { LocalDate } from '../../../core/model/local-date';
import { MonthYearItem } from '../../../core/model/period';
import {
  AmortizationPlanPaymentNoticePreview,
  AmortizationPlanPaymentNoticePreviewInstallment,
  AmortizationPlanPaymentNoticeRequest,
} from '../../model/amortization-plan-payment-notice';
import { AmortizationPlanService } from '../../service/data/amortization-plan.service';

@Component({
  standalone: false,
  selector: 'app-create-amortization-plan-payment-notice-sidebar',
  templateUrl: './create-amortization-plan-payment-notice-sidebar.component.html',
  styleUrls: ['./create-amortization-plan-payment-notice-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CreateAmortizationPlanPaymentNoticeSidebarComponent implements OnChanges, OnDestroy {
  readonly periodSelection = this.createPeriodSelection();

  constructor(
    private readonly _amortizationPlanService: AmortizationPlanService,
    private readonly _messageService: MessageService,
    private readonly _fb: FormBuilder,
    private readonly _fcf: FormControlExFactory
  ) {
    this.form = this._fb.group({
      dueFrom: this._fcf.createMandatory('AMORTIZATION_PLAN.PAYMENT_NOTICE.DUE_FROM', this.getDefaultDueFrom()),
      dueTo: this._fcf.createMandatory('AMORTIZATION_PLAN.PAYMENT_NOTICE.DUE_TO', this.getDefaultDueTo()),
    });

    this.form.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        if (!this.isLoading && !this.isSaving) {
          this.preview = null;
          this.previewLoaded = false;
        }
      });
  }

  @Input() amortizationPlanIds: string[] = [];

  @Output() created = new EventEmitter<void>();

  @Output() canceled = new EventEmitter<void>();

  readonly form: FormGroup;

  preview: AmortizationPlanPaymentNoticePreview;
  previewLoaded = false;
  isLoading = false;
  isSaving = false;
  private readonly _destroy$ = new Subject<void>();

  get hasRentInvoiceInstallments(): boolean {
    return (this.preview?.rentInvoiceInstallments?.length ?? 0) > 0;
  }

  get hasExcludedItems(): boolean {
    return (this.preview?.excludedItems?.length ?? 0) > 0;
  }

  get canPreview(): boolean {
    return !this.isLoading && !this.isSaving && !this.form.invalid && !this.hasInvalidPeriod && (this.amortizationPlanIds?.length ?? 0) > 0;
  }

  get canCreate(): boolean {
    return this.canPreview && this.previewLoaded && this.getCreateInstallmentCount() > 0;
  }

  get hasInvalidPeriod(): boolean {
    const dueFrom = this.form.controls['dueFrom'].value as MonthYearItem;
    const dueTo = this.form.controls['dueTo'].value as MonthYearItem;
    return dueFrom && dueTo ? this.isAfter(dueFrom, dueTo) : false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['amortizationPlanIds']) {
      this.loadContext();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  cancel(): void {
    this.canceled.emit();
  }

  previewPaymentNotices(): void {
    if (!this.canPreview) {
      return;
    }

    this.isLoading = true;
    this.form.disable({ emitEvent: false });

    this._amortizationPlanService.previewPaymentNotices(this.toRequest()).pipe(finalize(() => {
      this.isLoading = false;
    })).subscribe({
      next: preview => {
        this.preview = preview;
        this.previewLoaded = true;
        this.form.enable({ emitEvent: false });
      },
      error: error => {
        this.previewLoaded = false;
        this.form.enable({ emitEvent: false });
        this._messageService.showError(error);
      },
    });
  }

  submit(): void {
    if (!this.canCreate) {
      return;
    }

    this.isSaving = true;
    this.form.disable({ emitEvent: false });

    this._amortizationPlanService.createPaymentNotices(this.toRequest()).pipe(finalize(() => {
      this.isSaving = false;
    })).subscribe({
      next: preview => {
        this.preview = preview;
        this.previewLoaded = true;
        this.created.emit();
      },
      error: error => {
        this.form.enable({ emitEvent: false });
        this._messageService.showError(error);
      },
    });
  }

  onManualOverrideChanged(item: AmortizationPlanPaymentNoticePreviewInstallment, checked: boolean): void {
    if (!item || !item.canForceManual) {
      return;
    }

    item.manualOverrideSelected = checked;
  }

  private loadContext(): void {
    this.preview = null;
    this.previewLoaded = false;

    const amortizationPlanIds = (this.amortizationPlanIds ?? []).filter(id => !!id);
    if (!amortizationPlanIds.length) {
      return;
    }

    this.form.reset(
      {
        dueFrom: this.getDefaultDueFrom(),
        dueTo: this.getDefaultDueTo(),
      },
      { emitEvent: false }
    );

    this.previewPaymentNotices();
  }

  private toRequest(): AmortizationPlanPaymentNoticeRequest {
    const dueFrom = this.form.controls['dueFrom'].value as MonthYearItem;
    const dueTo = this.form.controls['dueTo'].value as MonthYearItem;
    return {
      amortizationPlanIds: (this.amortizationPlanIds ?? []).filter(id => !!id),
      dueFrom: {
        year: dueFrom?.year ?? null,
        month: dueFrom?.month ?? null,
      },
      dueTo: {
        year: dueTo?.year ?? null,
        month: dueTo?.month ?? null,
      },
      forcedManualInstallmentIds: this.getForcedManualInstallmentIds(),
    };
  }

  private getDefaultDueFrom(): MonthYearItem {
    const today = LocalDate.today();
    return this.findPeriod(today.year, today.month);
  }

  private getDefaultDueTo(): MonthYearItem {
    const dueFrom = this.getDefaultDueFrom();
    return this.findPeriod(dueFrom.year, dueFrom.month);
  }

  private getForcedManualInstallmentIds(): string[] {
    return (this.preview?.rentInvoiceInstallments ?? [])
      .filter(item => item.manualOverrideSelected && item.canForceManual && !!item.installmentId)
      .map(item => item.installmentId);
  }

  private getCreateInstallmentCount(): number {
    return (this.preview?.manualNoticeInstallments?.length ?? 0) + this.getForcedManualInstallmentIds().length;
  }

  private createPeriodSelection(): MonthYearItem[] {
    const periods: MonthYearItem[] = [];
    const today = new LocalDate(LocalDate.today().year, LocalDate.today().month, 1);

    for (let monthOffset = -24; monthOffset <= 60; monthOffset++) {
      const period = LocalDate.fromDate(new LocalDate(today.year, today.month, 1).addMonths(monthOffset));
      periods.push(new MonthYearItem(period.month, getMonthName(period.month), period.year));
    }

    return periods;
  }

  private findPeriod(year: number, month: number): MonthYearItem {
    return this.periodSelection.find(item => item.year === year && item.month === month)
      ?? new MonthYearItem(month, getMonthName(month), year);
  }

  private isAfter(left: MonthYearItem, right: MonthYearItem): boolean {
    return left.year > right.year || (left.year === right.year && left.month > right.month);
  }
}
